import type { Question, Option } from '../editor/types';
import { MAX_ANSWER_IMAGES, MAX_IMAGE_BYTES, textAnswerImages, textAnswerText, withTextAnswer } from '../editor/types';
import ImageLightbox from '../ImageLightbox';
import { useRef, useState } from 'react';

type FullOpt = { value: string; limit: number; used: number; onQuotaFull: 'jumpNext' | 'end' };

type Props = {
  question: Question;
  value: any;
  onChange: (v: any) => void;
  fullOptions?: FullOpt[];
};

function isFull(opts: FullOpt[] | undefined, value: string): FullOpt | undefined {
  if (!opts) return undefined;
  return opts.find((o) => o.value === value);
}

function normalizeOpt(raw: string | Option): Option {
  return typeof raw === 'string' ? { value: raw } : raw;
}

type TextQuestionInputProps = {
  value: unknown;
  onChange: (value: unknown) => void;
  allowImageUpload: boolean;
  maxLength?: number;
  multiline?: boolean;
};

/** 文本/多行文本输入：开启答案配图时答案升级为 { text, images } 对象（最多 3 张，单张 ≤1.5MB） */
function TextQuestionInput({ value, onChange, allowImageUpload, maxLength, multiline = false }: TextQuestionInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  const text = textAnswerText(value);
  const images = textAnswerImages(value);
  const hasStructuredAnswer = images.length > 0 || (value !== null && typeof value === 'object' && !Array.isArray(value));
  const setText = (nextText: string) => {
    onChange(allowImageUpload || hasStructuredAnswer ? withTextAnswer(nextText, images) : nextText);
  };
  const setImages = (nextImages: string[]) => onChange(withTextAnswer(text, nextImages));
  const handleFile = (file: File) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) {
      alert('请选择 PNG、JPEG、GIF 或 WebP 图片');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      alert('图片不能超过 1.5MB');
      return;
    }
    if (images.length >= MAX_ANSWER_IMAGES) {
      alert('最多上传 3 张图片');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') setImages([...images, reader.result]);
    };
    reader.readAsDataURL(file);
  };
  const field = multiline ? (
    <textarea
      className="textarea"
      rows={3}
      maxLength={maxLength}
      value={text}
      onChange={(e) => setText(e.target.value)}
      placeholder="请输入"
    />
  ) : (
    <input
      className="input"
      value={text}
      onChange={(e) => setText(e.target.value)}
      placeholder="请输入"
    />
  );
  if (!allowImageUpload) return field;
  return (
    <div className="text-answer-with-images">
      {field}
      <div className="answer-image-actions">
        <button
          type="button"
          className="answer-image-add"
          onClick={() => inputRef.current?.click()}
          disabled={images.length >= MAX_ANSWER_IMAGES}
        >
          <span aria-hidden="true">▧</span> 添加图片 ({images.length}/{MAX_ANSWER_IMAGES})
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
        />
      </div>
      {images.length > 0 && (
        <div className="answer-image-list">
          {images.map((image, index) => (
            <div className="answer-image-item" key={`${image.slice(0, 24)}-${index}`}>
              <button
                type="button"
                className="answer-image-preview"
                onClick={() => setLightbox({ src: image, alt: `回答图片 ${index + 1}` })}
                aria-label={`查看回答图片 ${index + 1}`}
              >
                <img src={image} alt={`回答图片 ${index + 1}`} />
              </button>
              <button
                type="button"
                className="answer-image-remove"
                onClick={() => setImages(images.filter((_, imageIndex) => imageIndex !== index))}
                aria-label={`删除回答图片 ${index + 1}`}
              >
                删除
              </button>
            </div>
          ))}
        </div>
      )}
      {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
    </div>
  );
}

export default function QuestionInput({ question, value, onChange, fullOptions }: Props) {
  const q = question;
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  switch (q.type) {
    case 'text':
      return (
        <TextQuestionInput
          value={value}
          allowImageUpload={Boolean((q.params as { allowImageUpload?: boolean } | undefined)?.allowImageUpload)}
          onChange={onChange}
        />
      );

    case 'longtext': {
      const maxLen = (q.params as any)?.maxLen;
      return (
        <TextQuestionInput
          value={value}
          maxLength={maxLen}
          multiline
          allowImageUpload={Boolean((q.params as { allowImageUpload?: boolean } | undefined)?.allowImageUpload)}
          onChange={onChange}
        />
      );
    }

    case 'single':
      return (
        <div className="radio-group">
          {(q.options ?? []).map((raw, i) => {
            const opt = normalizeOpt(raw);
            const full = isFull(fullOptions, opt.value);
            return (
              <label
                key={i}
                className={`radio-item ${full ? 'is-disabled' : ''} ${opt.image ? 'has-image' : ''}`}
                title={full ? `已满 ${full.used}/${full.limit}` : ''}
              >
                <div className="rq-meta">
                  <input
                    type="radio"
                    name={q.id}
                    checked={value === opt.value}
                    disabled={!!full}
                    onChange={() => onChange(opt.value)}
                  />
                  <span>{opt.value}</span>
                  {opt.quota !== undefined && (
                    <span className="quota-tag">配额 {opt.quota}</span>
                  )}
                  {full && <span className="full-tag">已满 {full.used}/{full.limit}</span>}
                </div>
                {opt.image && (
                  <button
                    type="button"
                    className="rq-opt-img-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLightbox({ src: opt.image!, alt: opt.value });
                    }}
                    title="点击查看大图"
                    aria-label="点击查看大图"
                  >
                    <img src={opt.image} alt={opt.value} className="rq-opt-img" />
                  </button>
                )}
              </label>
            );
          })}
          {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
        </div>
      );

    case 'multi': {
      const arr = Array.isArray(value) ? value : [];
      return (
        <div className="checkbox-group">
          {(q.options ?? []).map((raw, i) => {
            const opt = normalizeOpt(raw);
            const full = isFull(fullOptions, opt.value);
            const checked = arr.includes(opt.value);
            return (
              <label
                key={i}
                className={`checkbox-item ${full ? 'is-disabled' : ''} ${opt.image ? 'has-image' : ''}`}
                title={full ? `已满 ${full.used}/${full.limit}` : ''}
              >
                <div className="rq-meta">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!!full}
                    onChange={(e) => {
                      if (full) return;
                      const next = e.target.checked
                        ? [...arr, opt.value]
                        : arr.filter((x) => x !== opt.value);
                      onChange(next);
                    }}
                  />
                  <span>{opt.value}</span>
                  {opt.quota !== undefined && (
                    <span className="quota-tag">配额 {opt.quota}</span>
                  )}
                  {full && <span className="full-tag">已满 {full.used}/{full.limit}</span>}
                </div>
                {opt.image && (
                  <button
                    type="button"
                    className="rq-opt-img-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLightbox({ src: opt.image!, alt: opt.value });
                    }}
                    title="点击查看大图"
                    aria-label="点击查看大图"
                  >
                    <img src={opt.image} alt={opt.value} className="rq-opt-img" />
                  </button>
                )}
              </label>
            );
          })}
          {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
        </div>
      );
    }

    case 'slider': {
      const p = (q.params ?? { min: 0, max: 100, step: 1 }) as {
        min: number;
        max: number;
        step?: number;
        unit?: string;
      };
      const num = typeof value === 'number' ? value : p.min;
      return (
        <div className="slider-group">
          <input
            type="range"
            min={p.min}
            max={p.max}
            step={p.step}
            value={num}
            onChange={(e) => onChange(Number(e.target.value))}
          />
          <span className="slider-value">
            {num}
            {p.unit ?? ''}
          </span>
        </div>
      );
    }

    case 'select': {
      return (
        <div className="select" style={{ maxWidth: 360 }}>
          <select value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)}>
            <option value="">请选择</option>
            {(q.options ?? []).map((raw, i) => {
              const opt = normalizeOpt(raw);
              const full = isFull(fullOptions, opt.value);
              return (
                <option key={i} value={opt.value} disabled={!!full}>
                  {opt.value}{full ? `（已满）` : ''}
                </option>
              );
            })}
          </select>
        </div>
      );
    }

    case 'matrix': {
      const rows = q.params?.rows ?? [];
      const cols = q.params?.columns ?? [];
      const map = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
      return (
        <div className="matrix-wrap" style={{ overflowX: 'auto' }}>
          <table className="matrix-table" style={{ borderCollapse: 'collapse', minWidth: 360 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 10px' }}></th>
                {cols.map((c) => (
                  <th key={c} style={{ padding: '6px 10px', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r} style={{ borderTop: '1px solid var(--border, #e5e7eb)' }}>
                  <td style={{ padding: '8px 10px', fontSize: 14, whiteSpace: 'nowrap' }}>{r}</td>
                  {cols.map((c) => (
                    <td key={c} style={{ textAlign: 'center', padding: '8px 10px' }}>
                      <input
                        type="radio"
                        name={`${q.id}__${r}`}
                        checked={map[r] === c}
                        onChange={() => onChange({ ...map, [r]: c })}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case 'file':
      return <FileUpload value={value} onChange={onChange} />;

    default:
      return <div>未知题型</div>;
  }
}

/** 上传附件题：图片（自动压缩到 1280px 内）或 PDF（≤3MB）；答案 { name, dataUrl } → 提交后服务端落盘 */
function FileUpload({ value, onChange }: { value: any; onChange: (v: any) => void }) {
  const meta = value && typeof value === 'object' ? value : null;
  const pick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp,application/pdf';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const isImage = file.type.startsWith('image/');
      if (!isImage && file.size > 3 * 1024 * 1024) {
        alert('PDF 不能超过 3MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        if (!isImage || file.size <= 300 * 1024) {
          onChange({ name: file.name, dataUrl });
          return;
        }
        // 大图等比缩到 1280px 内转 JPEG，控制请求体体积
        const img = new Image();
        img.onload = () => {
          const max = 1280;
          const scale = Math.min(1, max / Math.max(img.width, img.height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(img.width * scale));
          canvas.height = Math.max(1, Math.round(img.height * scale));
          const ctx = canvas.getContext('2d')!;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const name = file.name.replace(/\.\w+$/, '') + '.jpg';
          onChange({ name, dataUrl: canvas.toDataURL('image/jpeg', 0.85) });
        };
        img.onerror = () => alert('图片读取失败，请换一张试试');
        img.src = dataUrl;
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };
  return (
    <div className="file-upload" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <button type="button" className="btn" onClick={pick}>
        {meta ? '重新选择附件' : '选择附件（图片 / PDF）'}
      </button>
      {meta && (
        <span className="hint" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          已选：{meta.name ?? '附件'}
          <button type="button" className="opt-del" onClick={() => onChange(undefined)} title="移除附件">✕</button>
        </span>
      )}
    </div>
  );
}